import { useEffect, useState } from "react";
import { X, ArrowLeft, ArrowRight, ArrowRightLeft, Banknote, Loader2, Plus, Store, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useCart } from "@/context/CartContext";
import { formatARS, API, BACKEND_URL } from "@/lib/format";
import { getAttribution } from "@/lib/attribution";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const resolveImg = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

const CLIENTE_KEY = "bp_checkout_cliente";

function leerClienteGuardado() {
  try {
    const raw = localStorage.getItem(CLIENTE_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d?.nombreCliente || !d?.telefono) return null;
    return d;
  } catch { return null; }
}

export default function CheckoutSheet({ open, onClose, totalFinal, evalStatus }) {
  const { items, subtotal, clear, setIsOpen, addItem } = useCart();
  const navigate = useNavigate();
  const effectiveTotal = totalFinal ?? subtotal;
  const evalBlocked = evalStatus === "loading" || evalStatus === "error";
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    nombreCliente: "",
    telefono: "",
    direccion: "",
    notas: "",
    metodoPago: "",
    montoEfectivo: "",
    retiro: false,
  });
  const [coords, setCoords] = useState(null);
  const [envio, setEnvio] = useState(null); // cotización del servidor
  const [envioError, setEnvioError] = useState("");
  const [clienteGuardado, setClienteGuardado] = useState(null);
  const [editandoDatos, setEditandoDatos] = useState(false);

  useEffect(() => {
    if (!open) return;
    const guardado = leerClienteGuardado();
    if (guardado) {
      setClienteGuardado(guardado);
      setEditandoDatos(false);
      setForm((f) => ({
        ...f,
        nombreCliente: guardado.nombreCliente,
        telefono: guardado.telefono,
        direccion: f.direccion || guardado.direccion || "",
      }));
    } else {
      setEditandoDatos(true);
    }
  }, [open]);

  const [suggestions, setSuggestions] = useState([]);
  const [payMethods, setPayMethods] = useState([]);
  const [shopOpen, setShopOpen] = useState(true);
  const [cfgMsgs, setCfgMsgs] = useState({ mensajeEnvioCorto: "", textoRetiro: "" });

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/config`).then((r) => {
      setShopOpen(r.data.abierto !== false);
      setCfgMsgs({ mensajeEnvioCorto: r.data.mensajeEnvioCorto || "", textoRetiro: r.data.textoRetiro || "" });
    }).catch(() => {});
  }, [open]);

  const cotizar = async (lat, lng) => {
    setEnvio(null);
    setEnvioError("");
    try {
      const { data } = await axios.post(`${API}/envios/cotizar`, {
        lat,
        lng,
        items: items.map((i) => ({ productoId: i.productoId, tamano: i.tamano, cantidad: i.cantidad, personalizacion: i.personalizacion || null })),
      }, { timeout: 15000 });
      if (data.ok) setEnvio(data);
      else setEnvioError(data.mensaje || "No llegamos a esa dirección");
    } catch (e) {
      setEnvioError(e?.response?.data?.detail || "No pudimos calcular el envío");
    }
  };

  const onDireccionTexto = (v) => {
    setForm({ ...form, direccion: v });
    if (coords) { setCoords(null); setEnvio(null); setEnvioError(""); }
  };

  const onDireccionElegida = ({ direccion, lat, lng }) => {
    setForm({ ...form, direccion });
    setCoords({ lat, lng });
    cotizar(lat, lng);
  };

  const pasarARetiro = () => {
    setForm({ ...form, retiro: true });
    setCoords(null);
    setEnvio(null);
    setEnvioError("");
  };

  useEffect(() => {
    if (!open || step !== 2) return;
    axios.get(`${API}/payment-methods`).then((r) => setPayMethods(r.data)).catch(() => {});
  }, [open, step]);

  useEffect(() => {
    if (!open || step !== 2) return;
    const visible = payMethods.filter((m) => (m.entregas || ["delivery", "retiro"]).includes(form.retiro ? "retiro" : "delivery"));
    if (visible.length && !visible.find((m) => m.id === form.metodoPago)) {
      setForm((f) => ({ ...f, metodoPago: visible[0].id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payMethods, form.retiro, open, step]);

  useEffect(() => {
    if (!open || step !== 2) return;
    axios.get(`${API}/menu`).then((r) => {
      const inCart = new Set(items.map((i) => i.productoId));
      const sug = r.data.filter((p) => p.esSugerido && !inCart.has(p.id) && p.activo !== false && !p.sinStock);
      setSuggestions(sug.slice(0, 3));
    }).catch(() => {});
  }, [open, step, items]);

  if (!open) return null;

  const selectedMethod = payMethods.find((m) => m.id === form.metodoPago);
  const entrega = form.retiro ? "retiro" : "delivery";
  const visibleMethods = payMethods.filter((m) =>
    (m.entregas || ["delivery", "retiro"]).includes(entrega) &&
    (m.tipo !== "en_local" || entrega === "retiro")
  );
  const costoEnvio = !form.retiro && envio?.ok ? envio.envio : 0;
  const totalConEnvio = effectiveTotal + costoEnvio;

  const canStep1 =
    form.nombreCliente.trim().length >= 2 &&
    form.telefono.replace(/\D/g, "").length >= 10 &&
    (form.retiro || (form.direccion.trim().length >= 4 && !!coords && !envioError && !!envio?.ok));

  const submit = async () => {
    if (!shopOpen) { toast.error("El local está cerrado en este momento"); return; }
    if (evalBlocked) return;
    setLoading(true);
    try {
      const payload = {
        nombreCliente: form.nombreCliente,
        telefono: form.telefono,
        direccion: form.retiro ? "" : form.direccion,
        notas: form.notas,
        metodoPago: form.metodoPago,
        montoEfectivo: selectedMethod?.tipo === "efectivo" && form.montoEfectivo
          ? Number(form.montoEfectivo)
          : null,
        total: totalConEnvio,
        origen: getAttribution(),
        entrega,
        lat: !form.retiro ? coords?.lat : null,
        lng: !form.retiro ? coords?.lng : null,
        items: items.map((i) => ({
          productoId: i.productoId,
          tamano: i.tamano,
          cantidad: i.cantidad,
          personalizacion: i.personalizacion || null,
        })),
      };
      const { data } = await axios.post(`${API}/orders`, payload);
      try {
        localStorage.setItem(CLIENTE_KEY, JSON.stringify({
          nombreCliente: form.nombreCliente.trim(),
          telefono: form.telefono.trim(),
          direccion: form.retiro ? "" : form.direccion.trim(),
        }));
      } catch { /* storage lleno o bloqueado */ }
      clear();
      setIsOpen(false);
      onClose();
      navigate(`/exito/${data.id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setEnvioError(!form.retiro ? msg : "");
      toast.error("No pudimos crear el pedido", { description: msg });
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div
        data-testid="checkout-sheet"
        className="relative w-full sm:max-w-lg max-h-[92vh] overflow-y-auto thin-scroll bg-[#0F0F11] border border-line sm:rounded-xl rounded-t-2xl p-6 slide-up"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex gap-1.5">
              {[1, 2].map((n) => (
                <div key={n} className={`h-1 w-8 rounded-full ${step >= n ? "bg-brand" : "bg-line"}`} />
              ))}
            </div>
            <span className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Paso {step}/2</span>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="checkout-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 1 && (
          <div data-testid="checkout-step-1-form">
            <h3 className="font-heading text-3xl uppercase text-white mb-1">Tus datos</h3>
            <p className="text-sm text-zinc-500 mb-6">Con esto te llevamos el pedido humeando 🔥</p>

            <div className="space-y-3">
              {clienteGuardado && !editandoDatos ? (
                <div data-testid="checkout-cliente-guardado" className="bg-panel border border-brand/40 rounded-lg p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-white font-bold truncate">{clienteGuardado.nombreCliente}</div>
                    <div className="text-xs text-zinc-400 truncate">{clienteGuardado.telefono}{clienteGuardado.direccion ? ` · ${clienteGuardado.direccion}` : ""}</div>
                  </div>
                  <button
                    data-testid="checkout-cambiar-datos"
                    onClick={() => setEditandoDatos(true)}
                    className="shrink-0 text-[10px] font-black uppercase tracking-widest text-gold hover:text-white border border-line hover:border-gold rounded px-3 py-2 transition-colors"
                  >
                    Cambiar datos
                  </button>
                </div>
              ) : (
                <>
                  <Field
                    label="Nombre"
                    testid="checkout-name-input"
                    value={form.nombreCliente}
                    onChange={(v) => setForm({ ...form, nombreCliente: v })}
                    placeholder="Juan Pérez"
                    autoComplete="name"
                  />
                  <Field
                    label="Teléfono (WhatsApp)"
                    testid="checkout-phone-input"
                    value={form.telefono}
                    onChange={(v) => setForm({ ...form, telefono: v })}
                    placeholder="11 5555 5555"
                    type="tel"
                    autoComplete="tel"
                  />
                </>
              )}
              <div>
                <span className="block text-[11px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Entrega</span>
                <div className="flex gap-2">
                  <button
                    data-testid="checkout-entrega-delivery"
                    onClick={() => setForm({ ...form, retiro: false })}
                    className={`flex-1 py-2.5 rounded border font-bold uppercase text-[11px] tracking-widest transition-colors flex items-center justify-center gap-1.5 ${!form.retiro ? "border-brand bg-brand/10 text-white" : "border-line text-zinc-500 hover:border-white/30"}`}
                  >
                    <Truck className="w-4 h-4" /> Delivery
                  </button>
                  <button
                    data-testid="checkout-entrega-retiro"
                    onClick={() => pasarARetiro()}
                    className={`flex-1 py-2.5 rounded border font-bold uppercase text-[11px] tracking-widest transition-colors flex items-center justify-center gap-1.5 ${form.retiro ? "border-brand bg-brand/10 text-white" : "border-line text-zinc-500 hover:border-white/30"}`}
                  >
                    <Store className="w-4 h-4" /> Retirar en el local
                  </button>
                </div>
                {form.retiro && cfgMsgs.textoRetiro && (
                  <p data-testid="checkout-texto-retiro" className="mt-1.5 text-xs text-gold">{cfgMsgs.textoRetiro}</p>
                )}
              </div>
              {!form.retiro && (
                <div>
                  <span className="block text-[11px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Dirección</span>
                  <AddressAutocomplete
                    testid="checkout-address-input"
                    value={form.direccion}
                    onChange={onDireccionTexto}
                    onSelect={onDireccionElegida}
                    placeholder="Av. Corrientes 1234, CABA"
                    autoComplete="street-address"
                  />
                  {!coords && form.direccion.trim().length >= 4 && (
                    <p className="mt-1.5 text-[11px] text-zinc-500">Elegí la dirección de la lista para calcular el envío</p>
                  )}
                  {envio?.ok && (
                    <div data-testid="checkout-envio-info" className="mt-2 p-2.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-xs">
                      <span className="text-emerald-300 font-bold">
                        Envío {envio.envio > 0 ? formatARS(envio.envio) : "gratis"}
                        {envio.zona ? ` · ${envio.zona} · reparte ${envio.reparte} · ${envio.distanciaKm} km` : ""}
                      </span>
                      {envio.faltaParaGratis > 0 && (
                        <div data-testid="checkout-falta-gratis" className="mt-1 text-gold font-bold">Te faltan {formatARS(envio.faltaParaGratis)} para el envío gratis</div>
                      )}
                    </div>
                  )}
                  {envioError && !form.retiro && (
                    <div data-testid="checkout-envio-error" className="mt-2 p-3 rounded-lg border border-red-500/40 bg-red-500/10">
                      <p className="text-xs text-red-300">{envioError}</p>
                      <button
                        data-testid="checkout-pasar-retiro"
                        onClick={pasarARetiro}
                        className="mt-2 flex items-center gap-1.5 bg-gold hover:bg-gold-strong text-black font-black uppercase text-[10px] px-3 py-2 rounded transition-colors"
                      >
                        <Store className="w-3.5 h-3.5" /> Mejor retiro por el local
                      </button>
                    </div>
                  )}
                </div>
              )}
              <Field
                label="Notas (opcional)"
                testid="checkout-notes-input"
                value={form.notas}
                onChange={(v) => setForm({ ...form, notas: v })}
                placeholder="Timbre, piso, sin cebolla..."
                as="textarea"
              />
            </div>

            <button
              data-testid="checkout-next-button"
              disabled={!canStep1}
              onClick={() => setStep(2)}
              className="mt-6 w-full flex items-center justify-center gap-2 bg-brand hover:bg-brand-strong disabled:bg-zinc-800 disabled:text-zinc-600 text-black font-black uppercase tracking-widest py-3 rounded transition-colors"
            >
              Continuar <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </button>
          </div>
        )}

        {step === 2 && (
          <div>
            <h3 className="font-heading text-3xl uppercase text-white mb-1">¿Cómo pagás?</h3>
            <p className="text-sm text-zinc-500 mb-4">
              Total: {evalBlocked ? <span className="text-zinc-400 font-bold">Calculando...</span> : <span data-testid="checkout-total-display" className="text-white font-bold">{formatARS(totalConEnvio)}</span>}
              {!form.retiro && envio?.ok && (
                <span data-testid="checkout-envio-line" className="block text-[11px] text-zinc-500 mt-0.5">
                  Productos {formatARS(effectiveTotal)} + envío {envio.envio > 0 ? formatARS(envio.envio) : "gratis"} ({envio.zona})
                </span>
              )}
            </p>

            {suggestions.length > 0 && (
              <div data-testid="checkout-upsell-section" className="mb-5 p-3 rounded-lg border border-gold/40 bg-gold/5">
                <div className="text-[11px] uppercase tracking-widest font-black text-gold mb-2">¿Sumás algo antes de pagar?</div>
                <div className="space-y-2">
                  {suggestions.map((s) => (
                    <div key={s.id} className="flex items-center gap-3">
                      <img src={resolveImg(s.imagen)} alt={s.nombre} className="w-10 h-10 rounded object-cover bg-black shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-white leading-tight line-clamp-1">{s.nombre}</div>
                        <div className="text-xs text-gold font-bold">{formatARS(s.precio)}</div>
                      </div>
                      <button
                        data-testid={`upsell-add-${s.id}`}
                        onClick={() => { addItem(s); toast.success(`${s.nombre} agregado`, { description: formatARS(s.precio) }); }}
                        className="flex items-center gap-1 bg-gold hover:bg-gold-strong text-black font-black uppercase text-[10px] px-3 py-1.5 rounded transition-colors shrink-0"
                      >
                        <Plus className="w-3 h-3" strokeWidth={3} /> Sumar
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3">
              {visibleMethods.length === 0 ? (
                <div className="text-zinc-500 text-sm py-2 flex items-center gap-2">
                  {payMethods.length === 0 ? <><Loader2 className="w-4 h-4 animate-spin" /> Cargando medios de pago...</> : "No hay medios de pago disponibles para esta modalidad"}
                </div>
              ) : (
                visibleMethods.map((m) => (
                  <PayOption
                    key={m.id}
                    testid={`checkout-payment-method-${m.id}`}
                    active={form.metodoPago === m.id}
                    onClick={() => setForm({ ...form, metodoPago: m.id })}
                    icon={m.tipo === "efectivo" ? <Banknote className="w-6 h-6" /> : <ArrowRightLeft className="w-6 h-6" />}
                    title={m.nombre}
                    subtitle={m.instrucciones || ""}
                    color={m.tipo === "efectivo" ? "#10B981" : "#009EE3"}
                  />
                ))
              )}

              {selectedMethod?.tipo === "efectivo" && (
                <Field
                  label="¿Con cuánto abonás? (opcional)"
                  testid="checkout-cash-amount-input"
                  value={form.montoEfectivo}
                  onChange={(v) => setForm({ ...form, montoEfectivo: v.replace(/\D/g, "") })}
                  placeholder="Ej: 20000"
                  type="tel"
                />
              )}
            </div>

            {!shopOpen && (
              <div data-testid="checkout-closed-warning" className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-xs font-bold uppercase tracking-widest text-center">
                El local está cerrado — no se pueden confirmar pedidos fuera de horario
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <button
                data-testid="checkout-back-button"
                onClick={() => setStep(1)}
                className="flex items-center justify-center gap-2 bg-panel border border-line hover:border-white text-white font-bold uppercase text-sm tracking-widest px-4 py-3 rounded transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Atrás
              </button>
              <button
                data-testid="checkout-submit-order-button"
                disabled={loading || evalBlocked || !shopOpen}
                onClick={submit}
                className="flex-1 flex items-center justify-center gap-2 bg-brand hover:bg-brand-strong disabled:bg-zinc-800 text-black font-black uppercase tracking-widest py-3 rounded transition-colors"
              >
                {!shopOpen ? "Local cerrado" : evalBlocked ? "Calculando total..." : loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Procesando...</> : "Confirmar pedido"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", as = "input", testid, autoComplete }) {
  const Comp = as;
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">{label}</span>
      <Comp
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        rows={as === "textarea" ? 2 : undefined}
        className="w-full bg-panel border border-line focus:border-brand rounded-md px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors"
      />
    </label>
  );
}

function PayOption({ active, onClick, icon, title, subtitle, color, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full flex items-center gap-4 p-4 rounded-lg border transition-colors text-left ${
        active ? "border-brand bg-brand/5" : "border-line bg-panel hover:border-white/30"
      }`}
    >
      <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: color + "20", color }}>
        {icon}
      </div>
      <div className="flex-1">
        <div className="font-bold text-white uppercase text-sm tracking-wide">{title}</div>
        <div className="text-xs text-zinc-500">{subtitle}</div>
      </div>
      <div className={`w-4 h-4 rounded-full border-2 ${active ? "bg-brand border-brand" : "border-zinc-600"}`} />
    </button>
  );
}
